import Cookies from "universal-cookie";
import { AlertModalCheckIcon } from "../../../../assets";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AppLayout } from "../../../../containers/layout/app";
import { ManageVaultWrapper } from "./styled";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../context/appContext";
import {
	Box,
	Grid2,
	Typography,
	CircularProgress,
	SelectChangeEvent,
} from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseButton } from "../../../../components/button/styled";
import { manageVaultService } from "../../../../utils/api/tellermanagement/vault/manageVault";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllSystemChargeService } from "../../../../utils/api/configuration/systemcharge/retrieveAllSystemCharge";
import { useDebounce } from "use-debounce";
import { retrieveSavingsAccountByAccountNumberService } from "../../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveCustomerService } from "../../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { retrieveGroupService } from "../../../../utils/api/customerservice/grouplist/retrieveGroup";
import { postManualChargeService } from "../../../../utils/api/tellermanagement/manualchargeposting/postManualCharge";

export const ManageVault = () => {
	const postingOptions = ["Buy from Vault", "Sell to Vault", "Manual Charge"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [formDetails, setFormDetails] = useState(() => {
		const action = " " as string;
		return {
			amount: "",
			action,
			...(action === "Manual Charge" && {
				charge: " ",
				accountName: "",
				accountNumber: "",
			}),
			...(["Buy from Vault", "Sell to Vault", " "].includes(action) && {
				description: "",
			}),
		};
	});
	const [allowOverride, setAllowOverride] = useState(true);
	const [debouncedAccountRef] = useDebounce(formDetails.accountNumber, 500);

	useEffect(() => {
		setActiveNavItem("Teller Management");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!debouncedAccountRef) return;
		const controller = new AbortController();
		let isActive = true;
		const fetchBankAccountInfo = async () => {
			try {
				setFormDetails((prev) => ({
					...prev,
					accountName: "",
				}));
				const savingsAccount =
					await retrieveSavingsAccountByAccountNumberService(
						TOKEN,
						debouncedAccountRef,
						controller.signal,
					);
				if (!isActive) return;
				if (!savingsAccount) {
					setFormDetails((prev) => ({
						...prev,
						accountName: "",
					}));
					return;
				}
				let customer: Record<string, any> | null = null;
				if (savingsAccount?.ownerType === "individual") {
					customer = await retrieveCustomerService(
						TOKEN,
						savingsAccount.ownerId,
						controller.signal,
					);
				} else {
					customer = await retrieveGroupService(
						TOKEN,
						savingsAccount.ownerId,
						controller.signal,
					);
				}
				if (!isActive) return;
				setFormDetails((prev) => ({
					...prev,
					accountName:
						savingsAccount?.ownerType === "individual"
							? `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`
							: (customer?.name ?? ""),
				}));
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch customer:", err);
					setFormDetails((prev) => ({
						...prev,
						accountName: "",
					}));
				}
			}
		};
		fetchBankAccountInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [TOKEN, debouncedAccountRef]);

	const { data: systemCharges } = useQuery({
		queryKey: ["charges", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllSystemChargeService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		if (formDetails?.charge && !formDetails?.charge.trim()) {
			setAllowOverride(true);
			return setFormDetails((prev) => ({
				...prev,
				amount: "",
			}));
		}
		const selectedCharge = systemCharges?.find(
			(charge: Record<string, any>) => charge.id === formDetails.charge,
		);
		if (selectedCharge) {
			setAllowOverride(selectedCharge.allowOverride === "yes" ? true : false);
			setFormDetails((prev) => ({
				...prev,
				amount: selectedCharge.amount.toString(),
			}));
		} else {
			setAllowOverride(true);
			setFormDetails((prev) => ({
				...prev,
				amount: "",
			}));
		}
	}, [formDetails.charge, systemCharges]);

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>,
		action: string,
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			let response = {} as { status: string };
			switch (action) {
				case "Buy from Vault":
					const buyId = "buy";
					response = await manageVaultService(TOKEN, buyId, formDetails);
					break;
				case "Sell to Vault":
					const sellId = "sell";
					response = await manageVaultService(TOKEN, sellId, formDetails);
					break;
				case "Manual Charge":
					response = await postManualChargeService(TOKEN, formDetails);
					break;
				default:
					break;
			}
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					`Failed to post transaction. Please check credentials and try again.`,
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to post transaction. ${error.message}`);
			console.error(`Failed to post transaction:`, error);
		}
	};

	return (
		<AppLayout pageId={"Vault"} pageTitle={"Teller Transaction"}>
			<ManageVaultWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title={"Transaction Successful"}
					message="Your transaction has been successfully completed."
					callToAction="OK"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<form onSubmit={(e) => handleSubmit(e, formDetails.action)}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Transaction Type{" "}
									<Typography
										component={"span"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"var(--text-salmon-pink-color)"}
									>
										*
									</Typography>
								</BaseLabel>
								<BaseSelect
									name="action"
									fontweight={400}
									value={formDetails.action}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Posting Type</BaseOption>
									{postingOptions?.map((option: string, index: number) => {
										return (
											<BaseOption key={index} value={option}>
												{option}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						{formDetails.action === "Manual Charge" && (
							<Grid2 size={{ mobile: 12, tablet: 6 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Account Number{" "}
										<Typography
											component={"span"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"var(--text-salmon-pink-color)"}
										>
											*
										</Typography>
									</BaseLabel>
									<BaseInput
										required
										name="accountNumber"
										onChange={(e) => handleChange(e)}
										placeholder="Enter Account Number"
										value={formDetails.accountNumber}
									/>
								</BaseFieldSet>
							</Grid2>
						)}
						{formDetails.action === "Manual Charge" && (
							<Grid2 size={{ mobile: 12, tablet: 6 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Customer Name{" "}
										<Typography
											component={"span"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"var(--text-salmon-pink-color)"}
										>
											*
										</Typography>
									</BaseLabel>
									<BaseInput
										required
										readOnly
										name="accountName"
										onChange={(e) => handleChange(e)}
										placeholder="Customer Account Name"
										value={formDetails.accountName}
									/>
								</BaseFieldSet>
							</Grid2>
						)}
						{formDetails.action === "Manual Charge" && (
							<Grid2 size={{ mobile: 12, tablet: 6 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Charge{" "}
										<Typography
											component={"span"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"var(--text-salmon-pink-color)"}
										>
											*
										</Typography>
									</BaseLabel>
									<BaseSelect
										name="charge"
										fontweight={400}
										value={formDetails?.charge ?? " "}
										colour="var(--semi-gray-color)"
										onChange={(e) => handleChange(e)}
									>
										<BaseOption value=" ">Select System Charge</BaseOption>
										{systemCharges?.map(
											(charge: Record<string, any>, index: number) => (
												<BaseOption key={index} value={charge.id}>
													{charge.name}
												</BaseOption>
											),
										)}
									</BaseSelect>
								</BaseFieldSet>
							</Grid2>
						)}
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Amount{" "}
									<Typography
										component={"span"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"var(--text-salmon-pink-color)"}
									>
										*
									</Typography>
								</BaseLabel>
								<BaseInput
									required
									name="amount"
									value={formDetails.amount}
									placeholder="Enter Amount"
									onChange={(e) => handleChange(e)}
									readOnly={allowOverride ? false : true}
								/>
							</BaseFieldSet>
						</Grid2>
						{["Buy from Vault", "Sell to Vault", " "].includes(
							formDetails.action,
						) && (
							<Grid2 size={{ mobile: 12, tablet: 6 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
									<BaseInput
										required
										name="description"
										value={formDetails.description}
										placeholder="Enter description."
										onChange={(e) => handleChange(e)}
									/>
								</BaseFieldSet>
							</Grid2>
						)}
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Post Transaction
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</ManageVaultWrapper>
		</AppLayout>
	);
};
